import { apiRequest } from './client';

const EMPLOYEE_FILE_FIELDS = ['profilePhoto', 'experienceLetter', 'resume', 'idCardPhoto', 'cancelledCheque', 'docBankPassbook', 'certificateMarksheet'];

const EMPLOYEE_PAYLOAD_FIELDS = [
  'employeeCode', 'firstName', 'lastName', 'email', 'phone', 'status', 'employmentStatus',
  'profilePhoto', 'dob', 'gender', 'maritalStatus', 'nationality', 'religion',
  'country', 'state', 'city', 'pincode', 'address',
  'correspondenceCountry', 'correspondenceState', 'correspondenceCity', 'correspondencePincode', 'correspondenceAddress',
  'fatherName', 'motherName', 'spouseName', 'children',
  'emergencyName', 'emergencyRelationship', 'emergencyPhone', 'emergencyAltPhone', 'emergencyAddress',
  'experience', 'experienceLetter', 'experiences', 'resume', 'height', 'weight', 'medicalBloodGroup', 'allergies', 'medicalCondition', 'disability',
  'linkedin', 'github', 'portfolio',
  'company', 'companyId', 'branch', 'branchId', 'department', 'departmentId', 'designation', 'designationId',
  'jobRole', 'employeeType', 'workType', 'deploymentLocation', 'officeLocation',
  'reportingManagerId', 'teamLeadId', 'hrManagerId',
  'joiningDate', 'confirmationDate', 'probationPeriod', 'probationEndDate',
  'contractStart', 'contractEnd', 'lastWorkingDate', 'exitDate',
  'shiftId', 'attendancePolicyId', 'workEmail', 'workPhone', 'extension', 'teamsId', 'slackId', 'skills', 'certifications', 'notes',
  'client', 'projectId', 'isClientEmployee', 'clientLocation', 'deploymentType', 'isBillable', 'billingRate',
  'bankName', 'bankBranch', 'accountHolder', 'accountHolderName', 'accountType', 'accountNumber',
  'ifsc', 'ifscCode', 'micrCode', 'swiftCode', 'upiId', 'cancelledCheque', 'docBankPassbook',
  'qualification', 'passingYear', 'instituteUniversity', 'certificateMarksheet', 'qualifications',
  'idCardType', 'documentNo', 'idCardPhoto', 'passportExpiry', 'idPassportExpiry', 'identityCards'
];

const NUMBER_FIELDS = new Set(['companyId', 'branchId', 'departmentId', 'designationId', 'reportingManagerId', 'teamLeadId', 'hrManagerId', 'shiftId', 'attendancePolicyId', 'projectId', 'billingRate']);
const ALLOCATION_NUMBER_FIELDS = new Set(['id', 'clientId', 'clientLocationId', 'projectId', 'allocationPercent', 'billingRate', 'reportingManagerId']);
const EMPLOYEE_INFO_SECTION_FIELDS = [
  'employeeCode', 'firstName', 'lastName', 'email', 'phone', 'status',
  'profilePhoto', 'dob', 'gender', 'maritalStatus', 'nationality', 'religion',
  'country', 'state', 'city', 'pincode', 'address',
  'correspondenceCountry', 'correspondenceState', 'correspondenceCity', 'correspondencePincode', 'correspondenceAddress',
  'fatherName', 'motherName', 'spouseName', 'children',
  'emergencyName', 'emergencyRelationship', 'emergencyPhone', 'emergencyAltPhone', 'emergencyAddress',
  'resume', 'height', 'weight', 'medicalBloodGroup', 'allergies', 'medicalCondition', 'disability',
  'linkedin', 'github', 'portfolio', 'qualifications', 'experiences', 'identityCards'
];
const WORK_INFO_SECTION_FIELDS = [
  'status', 'employmentStatus',
  'companyId', 'branchId', 'departmentId', 'designationId', 'jobRole', 'employeeType', 'workType', 'deploymentLocation', 'officeLocation',
  'reportingManagerId', 'teamLeadId', 'hrManagerId',
  'joiningDate', 'confirmationDate', 'probationPeriod', 'probationEndDate',
  'contractStart', 'contractEnd', 'lastWorkingDate', 'shiftId', 'attendancePolicyId',
  'workEmail', 'workPhone', 'extension', 'teamsId', 'slackId', 'skills', 'certifications', 'notes',
  'client', 'projectId', 'isClientEmployee', 'clientLocation', 'deploymentType',
  'isBillable', 'billingRate'
];
const BANK_INFO_SECTION_FIELDS = [
  'bankName', 'bankBranch', 'accountHolder', 'accountHolderName', 'accountType',
  'accountNumber', 'ifsc', 'ifscCode', 'micrCode', 'swiftCode', 'upiId',
  'cancelledCheque', 'docBankPassbook'
];

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '') || '';
}

function idValue(...values) {
  const value = firstValue(...values);
  return value === '' ? '' : String(value);
}

function toDateInputValue(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(date);
}

function getIdentityCards(employee) {
  const existingCards = firstValue(employee.identityCards, employee.identity_cards, employee.idCards, employee.id_cards);
  if (Array.isArray(existingCards)) {
    return existingCards.map((card) => ({
      ...card,
      documentNo: firstValue(card.documentNo, card.document_no, card.idCardNo, card.id_card_no)
    }));
  }

  const idCardType = employee.idCardType || employee.id_card_type || '';
  const documentNo = firstValue(employee.documentNo, employee.document_no, employee.idCardNo, employee.id_card_no, employee.aadhaarNo, employee.aadhaar_no);
  const idCardPhoto = employee.idCardPhoto || employee.id_card_photo || null;
  const passportExpiry = employee.passportExpiry || employee.passport_expiry || '';

  if (!idCardType && !documentNo && !idCardPhoto && !passportExpiry) return [];
  return [{ idCardType, documentNo, idCardPhoto, passportExpiry }];
}

export async function fetchEmployees(filters = {}, options = {}) {
  const response = await apiRequest('/employees', {
    ...options,
    query: {
      page: filters.page || 1,
      pageSize: filters.pageSize || 100,
      search: filters.search,
      department: filters.department,
      status: filters.status
    }
  });
  const payload = response?.data;
  if (Array.isArray(payload)) return { records: payload };
  return {
    records: Array.isArray(payload?.records) ? payload.records : [],
    pagination: payload?.pagination || null
  };
}

export async function fetchEmployeeOptions(context) {
  const response = await apiRequest(`/employees/options/${encodeURIComponent(context)}`);
  return {
    records: Array.isArray(response?.data) ? response.data : []
  };
}

export async function fetchEmployee(id, options = {}) {
  const response = await apiRequest(`/employees/${encodeURIComponent(id)}`, options);
  return { employee: response?.data || {} };
}

export async function fetchEmployeeHierarchy(id) {
  const response = await apiRequest(`/employees/${encodeURIComponent(id)}/hierarchy`);
  return response?.data || null;
}

export async function createEmployee(form) {
  return apiRequest('/employees', {
    method: 'POST',
    body: toEmployeeRequestBody(form, true)
  });
}

export async function updateEmployee(id, form) {
  return apiRequest(`/employees/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: toEmployeeRequestBody(form, false)
  });
}

export async function saveEmployeeInfoSection(id, form) {
  return apiRequest(id ? `/employees/${encodeURIComponent(id)}/sections/employee-info` : '/employees/sections/employee-info', {
    method: id ? 'PUT' : 'POST',
    body: toEmployeeRequestBody(form, !id, EMPLOYEE_INFO_SECTION_FIELDS)
  });
}

export async function saveWorkInfoSection(id, form) {
  const payload = toEmployeePayload(form, false, WORK_INFO_SECTION_FIELDS);
  EMPLOYEE_FILE_FIELDS.forEach((field) => delete payload[field]);
  delete payload.qualifications;
  delete payload.experiences;
  delete payload.identityCards;
  return apiRequest(`/employees/${encodeURIComponent(id)}/sections/work-info`, {
    method: 'PUT',
    body: payload
  });
}

export async function saveBankInfoSection(id, form) {
  return apiRequest(`/employees/${encodeURIComponent(id)}/sections/bank-info`, {
    method: 'PUT',
    body: toEmployeeRequestBody(form, false, BANK_INFO_SECTION_FIELDS)
  });
}

export async function deleteEmployee(id) {
  return apiRequest(`/employees/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
}

export async function fetchEmployeeAllocations(employeeId) {
  const response = await apiRequest(`/employees/${encodeURIComponent(employeeId)}/allocations`);
  return { records: Array.isArray(response?.data) ? response.data : [] };
}

export async function fetchEmployeeAssets(employeeId) {
  const response = await apiRequest(`/employees/${encodeURIComponent(employeeId)}/assets`);
  return { records: Array.isArray(response?.data) ? response.data : [] };
}

export async function saveEmployeeAllocation(employeeId, data = {}) {
  const allocationId = data.id;
  const path = allocationId
    ? `/employees/${encodeURIComponent(employeeId)}/allocations/${encodeURIComponent(allocationId)}`
    : `/employees/${encodeURIComponent(employeeId)}/allocations`;

  return apiRequest(path, {
    method: allocationId ? 'PUT' : 'POST',
    body: toEmployeeAllocationPayload(data)
  });
}

export async function deleteEmployeeAllocation(employeeId, allocationId) {
  return apiRequest(`/employees/${encodeURIComponent(employeeId)}/allocations/${encodeURIComponent(allocationId)}`, {
    method: 'DELETE'
  });
}

function toEmployeePayload(form, includeId = false, preserveFields = []) {
  const nestedIdCardPhoto = firstNestedFile(form.identityCards, 'idCardPhoto');
  const nestedCertificateMarksheet = firstNestedFile(form.qualifications, 'certificateMarksheet');
  const payload = {};

  EMPLOYEE_PAYLOAD_FIELDS.forEach((field) => {
    const value = normalizePayloadValue(form[field], field);
    if (value !== undefined) payload[field] = value;
  });

  const explicitStatus = form.status || form.employmentStatus;
  if (explicitStatus) {
    payload.status = explicitStatus;
    payload.employmentStatus = explicitStatus;
  }

  payload.employeeCode = firstValue(form.employeeCode, payload.employeeCode);
  payload.identityCards = normalizeIdentityCards(form.identityCards);
  payload.qualifications = normalizeQualifications(form.qualifications);
  payload.experiences = normalizeExperiences(form.experiences);
  if (form.idCardPhoto || nestedIdCardPhoto) payload.idCardPhoto = form.idCardPhoto || nestedIdCardPhoto;
  if (form.certificateMarksheet || nestedCertificateMarksheet) payload.certificateMarksheet = form.certificateMarksheet || nestedCertificateMarksheet;
  if (form.accountHolder && !payload.accountHolderName) payload.accountHolderName = form.accountHolder;
  if (form.ifsc && !payload.ifscCode) payload.ifscCode = form.ifsc;
  if (includeId && (form.recordId || form.id || form.employeeId)) payload.id = form.recordId || form.id || form.employeeId;

  const cleaned = cleanPayload(payload);
  preserveFields.forEach((field) => {
    if (!(field in form)) return;
    const value = form[field];
    if (NUMBER_FIELDS.has(field)) cleaned[field] = value === '' || value === undefined ? null : Number(value);
    else if (!isFileValue(value)) cleaned[field] = value ?? '';
  });
  return cleaned;
}

function isFileValue(value) {
  return typeof File !== 'undefined' && value instanceof File;
}

function containsFileValue(value) {
  if (isFileValue(value)) return true;
  if (Array.isArray(value)) return value.some(containsFileValue);
  if (value && typeof value === 'object') return Object.values(value).some(containsFileValue);
  return false;
}

function firstNestedFile(items, field) {
  if (!Array.isArray(items)) return null;
  return items.find((item) => isFileValue(item?.[field]))?.[field] || null;
}

function normalizePayloadValue(value, field) {
  if (value === '' || value === undefined || value === null) return undefined;
  if (isFileValue(value)) return value;
  if (NUMBER_FIELDS.has(field)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object' && 'name' in value) return value.name || undefined;
  return value;
}

function cleanPayload(payload) {
  return Object.entries(payload).reduce((result, [key, value]) => {
    if (value === undefined || value === null || value === '') return result;
    if (Array.isArray(value) && value.length === 0) return result;
    result[key] = value;
    return result;
  }, {});
}

function toEmployeeAllocationPayload(data = {}) {
  const payload = {
    id: normalizeAllocationValue(data.id, 'id'),
    allocationType: normalizeAllocationValue(data.allocationType, 'allocationType'),
    billableStatus: normalizeAllocationValue(data.billableStatus, 'billableStatus'),
    clientId: normalizeAllocationValue(data.clientId, 'clientId'),
    clientLocationId: normalizeAllocationValue(data.clientLocationId, 'clientLocationId'),
    projectId: normalizeAllocationValue(data.projectId, 'projectId'),
    projectIds: Array.isArray(data.projectIds) ? data.projectIds.map(Number).filter(Number.isFinite) : undefined,
    projectSource: normalizeAllocationValue(data.projectSource, 'projectSource'),
    allocationPercent: normalizeAllocationValue(data.allocationPercent, 'allocationPercent'),
    billingRate: normalizeAllocationValue(data.billingRate, 'billingRate'),
    billingCurrency: normalizeAllocationValue(data.billingCurrency, 'billingCurrency'),
    workLocation: normalizeAllocationValue(data.workLocation, 'workLocation'),
    reportingManagerId: normalizeAllocationValue(data.reportingManagerId, 'reportingManagerId'),
    clientApprover: normalizeAllocationValue(data.clientApprover, 'clientApprover'),
    startDate: normalizeAllocationValue(data.startDate, 'startDate'),
    endDate: normalizeAllocationValue(data.endDate, 'endDate'),
    status: normalizeAllocationValue(data.status, 'status'),
    approvalStatus: normalizeAllocationValue(data.approvalStatus, 'approvalStatus'),
    billingUnit: normalizeAllocationValue(data.billingUnit, 'billingUnit'),
    clientReportingManagerId: normalizeAllocationValue(data.clientReportingManagerId, 'clientReportingManagerId'),
    internalReportingManagerId: normalizeAllocationValue(data.internalReportingManagerId, 'internalReportingManagerId'),
    workingHoursPerDay: normalizeAllocationValue(data.workingHoursPerDay, 'workingHoursPerDay'),
    timesheetRequired: normalizeAllocationValue(data.timesheetRequired, 'timesheetRequired'),
    attendanceMethod: normalizeAllocationValue(data.attendanceMethod, 'attendanceMethod'),
    remarks: normalizeAllocationValue(data.remarks, 'remarks')
  };

  return cleanPayload(payload);
}

function normalizeAllocationValue(value, field) {
  if (value === '' || value === undefined || value === null) return undefined;
  if (ALLOCATION_NUMBER_FIELDS.has(field)) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  return value;
}

function normalizeIdentityCards(cards) {
  if (!Array.isArray(cards)) return [];
  return cards.map((card) => ({
    idCardType: card.idCardType || card.id_card_type || '',
    documentNo: card.documentNo || card.document_no || card.idCardNo || card.id_card_no || '',
    idCardPhoto: isFileValue(card.idCardPhoto) ? '' : (card.idCardPhoto || card.id_card_photo || ''),
    passportExpiry: card.passportExpiry || card.passport_expiry || card.idPassportExpiry || ''
  })).filter((card) => card.idCardType || card.documentNo || card.idCardPhoto || card.passportExpiry);
}

function normalizeQualifications(qualifications) {
  if (!Array.isArray(qualifications)) return [];
  return qualifications.map((item) => ({
    qualification: item.qualification || '',
    passingYear: item.passingYear || item.passing_year || '',
    instituteUniversity: item.instituteUniversity || item.institute_university || '',
    certificateMarksheet: isFileValue(item.certificateMarksheet)
      ? item.certificateMarksheet.name
      : (item.certificateMarksheet || item.certificate_marksheet || '')
  })).filter((item) => item.qualification || item.passingYear || item.instituteUniversity || item.certificateMarksheet);
}

function normalizeExperiences(experiences) {
  if (!Array.isArray(experiences)) return [];
  return experiences.map((item) => ({
    companyName: item.companyName || item.company_name || '',
    designation: item.designation || '',
    startDate: item.startDate || item.start_date || '',
    endDate: item.endDate || item.end_date || '',
    experience: item.experience || '',
    experienceLetter: isFileValue(item.experienceLetter)
      ? item.experienceLetter.name
      : (item.experienceLetter || item.experience_letter || '')
  })).filter((item) => item.companyName || item.designation || item.startDate || item.endDate || item.experience || item.experienceLetter);
}

function toEmployeeRequestBody(form, includeId = false, preserveFields = []) {
  const payload = toEmployeePayload(form, includeId, preserveFields);
  EMPLOYEE_FILE_FIELDS.forEach((field) => {
    if (form[field] !== undefined && form[field] !== null) {
      payload[field] = form[field];
    }
  });
  const nestedCertificateMarksheet = firstNestedFile(form.qualifications, 'certificateMarksheet');
  if (!isFileValue(payload.certificateMarksheet) && nestedCertificateMarksheet) {
    payload.certificateMarksheet = nestedCertificateMarksheet;
  }
  const hasFiles = EMPLOYEE_FILE_FIELDS.some((field) => isFileValue(payload[field]) || isFileValue(form[field]))
    || containsFileValue(form.identityCards)
    || containsFileValue(form.experiences);

  if (!hasFiles) {
    return payload;
  }

  const body = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (isFileValue(value)) {
      body.append(key, value);
    } else if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
      body.append(key, JSON.stringify(value));
    } else {
      body.append(key, value);
    }
  });
  (form.experiences || []).forEach((item) => {
    if (isFileValue(item?.experienceLetter)) body.append('experienceLetters', item.experienceLetter);
  });

  return body;
}

export function employeeToForm(employee) {
  return {
    ...employee,
    recordId: employee.id || employee.recordId || '',
    employeeId: employee.employeeOrgId || employee.employeeId || employee.employee_id || employee.id || '',
    employeeCode: firstValue(employee.employeeCode, employee.employee_code, employee.empCode, employee.emp_code),
    firstName: employee.firstName || '',
    lastName: employee.lastName || '',
    email: employee.email || employee.personalEmail || employee.personal_email || '',
    phone: employee.phone || employee.personalPhone || employee.personal_phone || '',
    department: employee.department || '',
    departmentId: idValue(employee.departmentId, employee.department_id),
    designation: employee.designation || '',
    designationId: idValue(employee.designationId, employee.designation_id),
    branch: employee.branch || employee.branchName || '',
    branchId: employee.branchId || employee.branch_id || '',
    company: employee.company || employee.companyName || '',
    companyId: employee.companyId || employee.company_id || '',
    country: employee.country || 'India',
    state: employee.state || '',
    city: employee.city || '',
    pincode: employee.pincode || '',
    address: employee.address || '',
    dob: toDateInputValue(employee.dob || employee.date_of_birth),
    gender: employee.gender || '',
    maritalStatus: employee.maritalStatus || employee.marital_status || '',
    nationality: employee.nationality || 'India',
    religion: employee.religion || '',
    fatherName: employee.fatherName || employee.father_name || '',
    motherName: employee.motherName || employee.mother_name || '',
    spouseName: employee.spouseName || employee.spouse_name || '',
    children: employee.children || '',
    emergencyName: employee.emergencyName || employee.emergency_name || '',
    emergencyRelationship: employee.emergencyRelationship || employee.emergency_relationship || '',
    emergencyPhone: employee.emergencyPhone || employee.emergency_phone || '',
    emergencyAltPhone: employee.emergencyAltPhone || employee.emergency_alt_phone || '',
    emergencyAddress: employee.emergencyAddress || employee.emergency_address || '',
    height: employee.height || '',
    weight: employee.weight || '',
    medicalBloodGroup: employee.medicalBloodGroup || employee.medical_blood_group || '',
    allergies: employee.allergies || '',
    medicalCondition: employee.medicalCondition || employee.medical_condition || '',
    disability: employee.disability || '',
    linkedin: employee.linkedin || '',
    github: employee.github || '',
    portfolio: employee.portfolio || '',
    qualification: employee.qualification || '',
    passingYear: employee.passingYear || employee.passing_year || '',
    instituteUniversity: employee.instituteUniversity || employee.institute_university || '',
    certificateMarksheet: employee.certificateMarksheet || employee.certificate_marksheet || null,
    qualifications: Array.isArray(employee.qualifications) ? employee.qualifications : [],
    experiences: Array.isArray(employee.experiences) ? employee.experiences : [],
    identityCards: getIdentityCards(employee),
    documentNo: firstValue(employee.documentNo, employee.document_no, employee.idCardNo, employee.id_card_no, employee.aadhaarNo, employee.aadhaar_no),
    correspondenceCountry: employee.correspondenceCountry || employee.correspondence_country || 'India',
    correspondenceState: employee.correspondenceState || employee.correspondence_state || '',
    correspondenceCity: employee.correspondenceCity || employee.correspondence_city || '',
    correspondencePincode: employee.correspondencePincode || employee.correspondence_pincode || '',
    correspondenceAddress: employee.correspondenceAddress || employee.correspondence_address || '',
    reportingManagerId: idValue(employee.reportingManagerId, employee.reporting_manager_id),
    reportingManager: firstValue(employee.reportingManager, employee.reporting_manager),
    reportingManagerCode: firstValue(employee.reportingManagerCode, employee.reporting_manager_code),
    teamLeadId: idValue(employee.teamLeadId, employee.team_lead_id),
    teamLead: firstValue(employee.teamLead, employee.team_lead),
    teamLeadCode: firstValue(employee.teamLeadCode, employee.team_lead_code),
    hrManagerId: idValue(employee.hrManagerId, employee.hr_manager_id),
    hrManager: firstValue(employee.hrManager, employee.hr_manager),
    hrManagerCode: firstValue(employee.hrManagerCode, employee.hr_manager_code),
    status: employee.status || employee.employmentStatus || employee.employment_status || 'Active',
    employeeType: employee.employeeType || employee.employmentType || '',
    workType: employee.workType || employee.employmentType || '',
    deploymentLocation: firstValue(employee.deploymentLocation, employee.deployment_location) || 'Internal Office',
    shiftId: idValue(employee.shiftId, employee.shift_id),
    shift: firstValue(employee.shift, employee.shiftName, employee.shift_name),
    attendancePolicyId: idValue(employee.attendancePolicyId, employee.attendance_policy_id),
    attendancePolicyName: firstValue(employee.attendancePolicyName, employee.attendance_policy_name, employee.policyName, employee.policy_name),
    joiningDate: toDateInputValue(employee.joiningDate || employee.joining_date),
    confirmationDate: toDateInputValue(employee.confirmationDate || employee.confirmation_date),
    probationPeriod: firstValue(employee.probationPeriod, employee.probation_period),
    probationEndDate: toDateInputValue(employee.probationEndDate || employee.probation_end_date),
    contractStart: toDateInputValue(employee.contractStart || employee.contract_start),
    contractEnd: toDateInputValue(employee.contractEnd || employee.contract_end),
    lastWorkingDate: toDateInputValue(employee.lastWorkingDate || employee.last_working_date),
    officeLocation: firstValue(employee.officeLocation, employee.office_location),
    jobRole: firstValue(employee.jobRole, employee.job_role),
    workEmail: firstValue(employee.workEmail, employee.work_email),
    workPhone: firstValue(employee.workPhone, employee.work_phone),
    client: employee.client || '',
    projectId: employee.projectId || employee.project_id || '',
    isClientEmployee: employee.isClientEmployee || employee.is_client_employee || 'No',
    clientLocation: employee.clientLocation || employee.client_location || '',
    deploymentType: employee.deploymentType || employee.deployment_type || '',
    isBillable: employee.isBillable || employee.is_billable || 'Billable',
    billingRate: employee.billingRate || employee.billing_rate || '',
    profilePhoto: firstValue(employee.profilePhoto, employee.profile_photo, employee.profileImage, employee.profileImageUrl, employee.avatarUrl) || null,
    experienceLetter: employee.experienceLetter || employee.experience_letter || null,
    resume: employee.resume || null,
    certificateMarksheet: employee.certificateMarksheet || employee.certificate_marksheet || null,
    qualifications: Array.isArray(employee.qualifications) ? employee.qualifications : [],
    experiences: Array.isArray(employee.experiences) ? employee.experiences : [],
    identityCards: getIdentityCards(employee),
    documentNo: firstValue(employee.documentNo, employee.document_no, employee.idCardNo, employee.id_card_no, employee.aadhaarNo, employee.aadhaar_no),
    correspondenceCountry: employee.correspondenceCountry || employee.correspondence_country || 'India',
    correspondenceState: employee.correspondenceState || employee.correspondence_state || '',
    correspondenceCity: employee.correspondenceCity || employee.correspondence_city || '',
    correspondencePincode: employee.correspondencePincode || employee.correspondence_pincode || '',
    correspondenceAddress: employee.correspondenceAddress || employee.correspondence_address || '',
    reportingManager: employee.manager || employee.reportingManager || '',
    status: employee.employmentStatus || employee.status || employee.employment_status || 'Active',
    employmentStatus: employee.employmentStatus || employee.status || employee.employment_status || 'Active',
    employeeType: employee.employeeType || employee.employmentType || '',
    workType: employee.workType || employee.employmentType || '',
    deploymentLocation: firstValue(employee.deploymentLocation, employee.deployment_location) || 'Internal Office',
    shiftId: idValue(employee.shiftId, employee.shift_id),
    shift: firstValue(employee.shift, employee.shiftName, employee.shift_name),
    attendancePolicyId: idValue(employee.attendancePolicyId, employee.attendance_policy_id),
    attendancePolicyName: firstValue(employee.attendancePolicyName, employee.attendance_policy_name, employee.policyName, employee.policy_name),
    joiningDate: toDateInputValue(employee.joiningDate || employee.joining_date),
    profilePhoto: firstValue(employee.profilePhoto, employee.profile_photo, employee.profileImage, employee.profileImageUrl, employee.avatarUrl) || null,
    experienceLetter: employee.experienceLetter || employee.experience_letter || null,
    resume: employee.resume || null,
    idCardPhoto: employee.idCardPhoto || null,
    accountHolder: employee.accountHolder || employee.accountHolderName || '',
    bankName: firstValue(employee.bankName, employee.bank_name),
    bankBranch: firstValue(employee.bankBranch, employee.bank_branch),
    accountType: firstValue(employee.accountType, employee.account_type),
    accountNumber: firstValue(employee.accountNumber, employee.account_number),
    ifsc: employee.ifsc || employee.ifscCode || '',
    micrCode: firstValue(employee.micrCode, employee.micr_code),
    swiftCode: firstValue(employee.swiftCode, employee.swift_code),
    upiId: firstValue(employee.upiId, employee.upi_id),
    cancelledCheque: employee.cancelledCheque || employee.cancelledChequeFile || null,
    docBankPassbook: employee.docBankPassbook || employee.bankPassbookFile || null,
    confirmAccount: employee.accountNumber || '',
    lwf: employee.lwf || employee.lwfNumber || ''
  };
}

export function toEmployeeRow(employee) {
  const name = `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || employee.employeeCode;
  const recordId = employee.id;
  const employeeCode = employee.employeeCode || employee.employee_code || String(recordId || '');
  return {
    id: employee.id,
    employeeCode,
    name,
    avatar: name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase(),
    profilePhoto: firstValue(employee.profilePhoto, employee.profile_photo, employee.profileImage, employee.profileImageUrl, employee.avatarUrl, employee.photo, employee.image),
    designation: employee.designation || '',
    department: employee.department || '',
    status: employee.employmentStatus || employee.status || employee.employment_status || 'Active',
    joiningDateRaw: employee.joiningDate || employee.joining_date || '',
    joining: formatDisplayDate(employee.joiningDate || employee.joining_date),
    email: employee.email || employee.personalEmail || '',
    phone: employee.phone || employee.personalPhone || '',
    workType: employee.workType || employee.employmentType || '',
    shift: employee.shift || '',
    reportingManager: firstValue(
      employee.reportingManager,
      employee.reportingManagerName,
      employee.reporting_manager,
      employee.reporting_manager_name,
      employee.manager,
      employee.managerName,
      employee.manager_name
    ),
    reportingManagerId: idValue(employee.reportingManagerId, employee.reporting_manager_id),
    teamLead: firstValue(
      employee.teamLead,
      employee.teamLeadName,
      employee.team_lead,
      employee.team_lead_name
    ),
    teamLeadId: idValue(employee.teamLeadId, employee.team_lead_id),
    hrManager: firstValue(
      employee.hrManager,
      employee.hrManagerName,
      employee.hr_manager,
      employee.hr_manager_name
    ),
    hrManagerId: idValue(employee.hrManagerId, employee.hr_manager_id)
  };
}
